import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabClient.module.css';
import MetaHead from '../components/MetaHead';
import { calculateContrastRatio, passesWCAG_AA } from '../utils/wcagContrast';

/* ============================================================
   TabClient — onglet "Page client" (funnel public)
   ------------------------------------------------------------
   Simulation iPhone du parcours côté client + éditeur des messages
   et couleurs de la landing publique.

   IDs DOM préservés (backend Express) :
     #client-preview-iphone  · conteneur de l'iPhone
     #client-step-tabs       · sélecteur d'étape
     #client-msg-welcome     · textarea message d'accueil
     #client-msg-thanks      · textarea message de remerciement
     #client-msg-feedback    · textarea message feedback privé
     #client-color-primary   · input couleur principale
     #client-color-bg        · input couleur de fond
     #client-resto-name      · input nom resto

   Callbacks :
     onSave(payload)        ← sauverPageClient()
     onPreview()            ← ouvrirPreviewExterne()
     onReset()              ← resetCustomization()

   Note (Sprint 2 P3) : l'upload de logo est désactivé tant que la route
   POST /api/restaurants/logo n'est pas livrée. Le preview du logo
   existant reste visible et le bouton "Retirer" reste fonctionnel.
   ============================================================ */

// Steps avec leur emoji + clés i18n (label + desc résolus en runtime via `t`).
const STEPS = [
  { id: 'welcome',  emoji: '👋', labelKey: 'client.step_welcome_label',  descKey: 'client.step_welcome_desc' },
  { id: 'rating',   emoji: '⭐', labelKey: 'client.step_rating_label',   descKey: 'client.step_rating_desc' },
  { id: 'thanks',   emoji: '🌟', labelKey: 'client.step_thanks_label',   descKey: 'client.step_thanks_desc' },
  { id: 'feedback', emoji: '💬', labelKey: 'client.step_feedback_label', descKey: 'client.step_feedback_desc' },
];

/* ============================================================
   PhoneFrame — bezel iPhone réutilisable
============================================================ */
function PhoneFrame({ children, restoName }) {
  return (
    <div className={styles.phone}>
      <div className={styles.phoneNotch} />
      <div className={styles.phoneScreen}>
        <div className={styles.phoneStatus}>
          <span>9:41</span>
          <span className={styles.statusIcons}>
            <span>•••</span><span>📶</span><span>🔋</span>
          </span>
        </div>
        <div className={styles.phoneAddr}>
          <span className={styles.addrLock}>🔒</span>
          <span className={styles.addrUrl}>revyo.fr/{(restoName || 'resto').toLowerCase().replace(/\s+/g, '-').slice(0, 20)}</span>
        </div>
        <div className={styles.phoneContent}>{children}</div>
      </div>
      <div className={styles.phoneHomeBar} />
    </div>
  );
}

/* ============================================================
   StepRenderer — rend la maquette du step actif
============================================================ */
function StepRenderer({ step, config, t }) {
  const { restoName, msgWelcome, msgThanks, msgFeedback, colorPrimary, colorBg, logoUrl } = config;
  const accent = colorPrimary || '#E8502A';
  const bg = colorBg || '#FBF7F0';

  const screenStyle = { background: bg };

  if (step === 'welcome') {
    return (
      <div id="cs-1" className={styles.funnelScreen} style={screenStyle}>
        {logoUrl ? (
          <img src={logoUrl} alt="" className={styles.funnelLogo} />
        ) : (
          <div className={styles.funnelLogoPh} style={{ background: accent }}>
            {(restoName || 'R')[0]}
          </div>
        )}
        <h2 className={styles.funnelTitle}>{restoName || t('client.funnel_resto_fallback')}</h2>
        <p className={styles.funnelMsg}>{msgWelcome}</p>
        <div id="cs-sms-field" className={styles.funnelField}>
          <label className={styles.funnelFieldLabel}>{t('client.funnel_phone_label')}</label>
          <input id="cs-phone" type="tel" className={styles.funnelInput} placeholder={t('client.funnel_phone_placeholder')} readOnly />
        </div>
        <div id="cs-email-field" className={styles.funnelField}>
          <label className={styles.funnelFieldLabel}>{t('client.funnel_email_label')}</label>
          <input id="cs-email" type="email" className={styles.funnelInput} placeholder={t('client.funnel_email_placeholder')} readOnly />
        </div>
        <p id="cs-consent-text" className={styles.funnelConsent}>
          {t('client.funnel_consent')}
        </p>
        <button id="cs-submit-btn" className={styles.funnelBtn} style={{ background: accent }}>
          {t('client.funnel_submit_btn')}
        </button>
      </div>
    );
  }

  if (step === 'rating') {
    return (
      <div className={styles.funnelScreen} style={screenStyle}>
        <div className={styles.funnelLogoPh} style={{ background: accent }}>
          {(restoName || 'R')[0]}
        </div>
        <h2 className={styles.funnelTitle}>{t('client.funnel_rating_title')}</h2>
        <div className={styles.funnelStars}>
          {[1, 2, 3, 4, 5].map((s) => (
            <span key={s} style={{ color: accent }}>★</span>
          ))}
        </div>
        <p className={styles.funnelHint}>{t('client.funnel_rating_hint')}</p>
      </div>
    );
  }

  if (step === 'thanks') {
    return (
      <div id="cs-2-happy" className={styles.funnelScreen} style={screenStyle}>
        <div className={styles.funnelEmoji}>🌟</div>
        <h2 id="cs-thanks-google" className={styles.funnelTitle}>{msgThanks?.split('\n')[0] || t('client.funnel_thanks_default_title')}</h2>
        <p className={styles.funnelMsg}>{msgThanks?.split('\n').slice(1).join('\n') || t('client.funnel_thanks_default_body')}</p>
        <button className={styles.funnelBtn} style={{ background: accent }}>
          {t('client.funnel_thanks_btn')}
        </button>
        <button className={styles.funnelBtnGhost}>{t('client.funnel_thanks_later')}</button>
      </div>
    );
  }

  // feedback
  return (
    <div id="cs-2-bad" className={styles.funnelScreen} style={screenStyle}>
      <div className={styles.funnelEmoji}>🙏</div>
      <h2 id="cs-thanks-private" className={styles.funnelTitle}>{msgFeedback?.split('\n')[0] || t('client.funnel_feedback_default_title')}</h2>
      <p className={styles.funnelMsg}>{msgFeedback?.split('\n').slice(1).join('\n') || t('client.funnel_feedback_default_body')}</p>
      <textarea
        id="cs-review"
        className={styles.funnelTextarea}
        placeholder={t('client.funnel_feedback_placeholder')}
        readOnly
      />
      <button className={styles.funnelBtn} style={{ background: accent }}>{t('client.funnel_feedback_btn')}</button>
    </div>
  );
}

/* ============================================================
   TabClient
============================================================ */
export default function TabClient({
  config: initialConfig = {},
  onSave,
  onPreview,
  onReset,
}) {
  const { t } = useTranslation('tabs');
  // Construit le state à partir de la config reçue du container. Le nom du resto
  // n'a PLUS de fallback hardcodé ("Le Petit Bistrot") : il vient de
  // `restaurants.nom`. Seuls les messages et couleurs ont des defaults génériques.
  const buildConfig = useCallback((c) => ({
    restoName: c.restoName ?? '',
    msgWelcome: c.msgWelcome || t('client.default_msg_welcome'),
    msgThanks: c.msgThanks || t('client.default_msg_thanks'),
    msgFeedback: c.msgFeedback || t('client.default_msg_feedback'),
    colorPrimary: c.colorPrimary || '#E8502A',
    colorBg: c.colorBg || '#FBF7F0',
    logoUrl: c.logoUrl ?? null,
  }), [t]);

  const [config, setConfig] = useState(() => buildConfig(initialConfig));
  // Re-synchronise le state interne quand le container fournit (ou réinitialise)
  // la config — le fetch async résout APRÈS le premier mount, donc sans ça le
  // formulaire resterait bloqué sur les defaults. C'est la cause du bug "Le Petit
  // Bistrot" affiché pour tous les comptes.
  useEffect(() => {
    setConfig(buildConfig(initialConfig));
  }, [initialConfig, buildConfig]);

  const [activeStep, setActiveStep] = useState('welcome');
  const [saving, setSaving] = useState(false);

  const update = useCallback((k, v) => {
    setConfig((prev) => ({ ...prev, [k]: v }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setSaving(true);
    try { await onSave(config); }
    finally { setSaving(false); }
  }, [config, onSave]);

  return (
    <>
      <MetaHead page="client" />
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1><span className={styles.titleEmoji}>{t('client.title_emoji')}</span>{t('client.title')}</h1>
          <p>{t('client.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.btnGhost} onClick={onReset}>{t('client.btn_reset')}</button>
          <button type="button" className={styles.btnGhost} onClick={onPreview}>{t('client.btn_preview')}</button>
          <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? t('client.btn_saving') : t('client.btn_save')}
          </button>
        </div>
      </header>

      <div className={styles.layout}>
        {/* ===== Editor ===== */}
        <section className={styles.editor}>
          {/* Identité */}
          <div className={styles.editorSection}>
            <h3 className={styles.sectionTitle}>{t('client.section_identity_title')}</h3>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('client.field_resto_name')}</span>
              <input
                id="client-resto-name"
                type="text"
                className={styles.input}
                value={config.restoName}
                onChange={(e) => update('restoName', e.target.value)}
              />
            </label>
            <label className={styles.fieldFile}>
              <span className={styles.fieldLabel}>{t('client.field_logo')}</span>
              <div className={styles.fileRow}>
                <div className={styles.logoPreview}>
                  {config.logoUrl ? (
                    <img src={config.logoUrl} alt="" />
                  ) : (
                    <span style={{ background: config.colorPrimary }}>{config.restoName?.[0] || 'R'}</span>
                  )}
                </div>
                {config.logoUrl && (
                  <button type="button" className={styles.linkBtn} onClick={() => update('logoUrl', null)}>
                    {t('client.logo_remove')}
                  </button>
                )}
              </div>
            </label>
          </div>

          {/* Couleurs */}
          <div className={styles.editorSection}>
            <h3 className={styles.sectionTitle}>{t('client.section_colors_title')}</h3>
            <div className={styles.colorRow}>
              <label className={styles.colorField}>
                <span className={styles.fieldLabel}>{t('client.field_color_primary')}</span>
                <div className={styles.colorBox}>
                  <input
                    id="client-color-primary"
                    type="color"
                    value={config.colorPrimary}
                    onChange={(e) => update('colorPrimary', e.target.value)}
                  />
                  <span>{config.colorPrimary.toUpperCase()}</span>
                </div>
                {/* Indicateur de contraste WCAG vs texte blanc — non bloquant */}
                {(() => {
                  const ratio = calculateContrastRatio(config.colorPrimary, '#FFFFFF');
                  if (ratio === null) return null;
                  const ok = passesWCAG_AA(ratio);
                  const ratioStr = ratio.toFixed(1);
                  return (
                    <div
                      className={`${styles.contrastIndicator} ${ok ? styles.contrastOk : styles.contrastWarn}`}
                    >
                      {ok
                        ? t('client.contrast_ok', { ratio: ratioStr })
                        : t('client.contrast_warn', { ratio: ratioStr })}
                      {!ok && (
                        <span className={styles.contrastHint}>
                          {t('client.contrast_hint')}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </label>
              <label className={styles.colorField}>
                <span className={styles.fieldLabel}>{t('client.field_color_bg')}</span>
                <div className={styles.colorBox}>
                  <input
                    id="client-color-bg"
                    type="color"
                    value={config.colorBg}
                    onChange={(e) => update('colorBg', e.target.value)}
                  />
                  <span>{config.colorBg.toUpperCase()}</span>
                </div>
              </label>
            </div>
            <div className={styles.swatchHint}>
              {t('client.swatch_suggestions')}
              {['#E8502A', '#0A7A45', '#2563EB', '#7C3AED', '#1F2937'].map((c) => (
                <button
                  key={c}
                  type="button"
                  className={styles.swatch}
                  style={{ background: c }}
                  onClick={() => update('colorPrimary', c)}
                  aria-label={t('client.swatch_label', { hex: c })}
                />
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className={styles.editorSection}>
            <h3 className={styles.sectionTitle}>{t('client.section_messages_title')}</h3>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('client.field_msg_welcome')}</span>
              <textarea
                id="client-msg-welcome"
                className={styles.textarea}
                rows={2}
                value={config.msgWelcome}
                onChange={(e) => update('msgWelcome', e.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('client.field_msg_thanks')}</span>
              <textarea
                id="client-msg-thanks"
                className={styles.textarea}
                rows={3}
                value={config.msgThanks}
                onChange={(e) => update('msgThanks', e.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('client.field_msg_feedback')}</span>
              <textarea
                id="client-msg-feedback"
                className={styles.textarea}
                rows={3}
                value={config.msgFeedback}
                onChange={(e) => update('msgFeedback', e.target.value)}
              />
            </label>
          </div>
        </section>

        {/* ===== Preview ===== */}
        <aside className={styles.previewSide}>
          <div className={styles.previewLabel}>
            <span>{t('client.preview_label')}</span>
            <span className={styles.previewHint}>{t('client.preview_hint')}</span>
          </div>

          <div id="client-step-tabs" className={styles.stepTabs}>
            {STEPS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`${styles.stepTab} ${activeStep === s.id ? styles.stepTabActive : ''}`}
                onClick={() => setActiveStep(s.id)}
              >
                <span className={styles.stepEmoji}>{s.emoji}</span>
                <span className={styles.stepLabel}>{t(s.labelKey)}</span>
              </button>
            ))}
          </div>

          <div id="client-preview-iphone" className={styles.phoneWrap}>
            <PhoneFrame restoName={config.restoName}>
              <StepRenderer step={activeStep} config={config} t={t} />
            </PhoneFrame>
          </div>

          <div className={styles.stepDesc}>
            {(() => {
              const active = STEPS.find((s) => s.id === activeStep);
              return active ? t(active.descKey) : '';
            })()}
          </div>
        </aside>
      </div>
    </>
  );
}
